<script setup lang="ts">
import type { PropType } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { getApiRoot } from '~/utilities/api';

const numberOfCollectionsToShow = 10;

const props = defineProps({
  modelValue: String as PropType<string>,
});
const emit = defineEmits(['update:modelValue']);

const idSubfix = Math.random().toString(36).substring(2, 9);

// console.log('CollectionFilter: Using random subfix ', idSubfix);

// What to do if no v-model is given? Use a local variable that works and kepp them somewhat in sync?
const collectionSearchString = ref('');
const modelValueRadioSelectedCollection = toRef(props, 'modelValue');

const collectionMatches = ref([]);

// If a collection is selected and the search is changed so that
// it does not appear anymore, it is still shown and intractable
const collectionMatchesSticky = ref([]);

async function setupWithExternalData() {
  if (modelValueRadioSelectedCollection !== undefined && modelValueRadioSelectedCollection.value !== '') {
    let stickyCollectionGivenByQuery = true;

    for (const col of collectionMatches.value) {
      if (col.id === modelValueRadioSelectedCollection.value) {
        stickyCollectionGivenByQuery = false;
      }
    }

    if (stickyCollectionGivenByQuery) {
      // console.log(idSubfix + ': Externally given sticky collection');
      const collectionData = await useFetch(`${getApiRoot()}/collections/${modelValueRadioSelectedCollection.value}`);
      const collectionName = collectionData.data.value.name;
      collectionMatchesSticky.value.push({
        id: modelValueRadioSelectedCollection.value,
        name: collectionName,
        thumbnail_uri: collectionData.data.value.thumbnail_uri,
        checked: true,
      });
      await fetchMatchedCollections();
    }
    await correctRadioButtonsChecked();
  } else {
    await fetchMatchedCollections();
  }
}

// Clicking a checked radio button should uncheck it => some overwrite-mechanism is needed.
async function correctRadioButtonsChecked() {
  // console.log(idSubfix + ': correctRadioButtonsChecked');

  // When using this component multiple times in sync, another instance might select a collection that is not shown
  // right now, as the search string is not synchronized.
  // Solution A: Synchronize Search Strings => Lots of API calls, bad.
  // Solution B: Fetch the data for the changed selection, if needed (done here)
  // Solution C: Ignore. This component should be used only one anyway, even on events page,
  //             as the screen is either small or large in a normal use case.
  // Solution D: Trigger update and data fetch when the layout changes from mobile filter to normal.
  let foundSelectedCollection = modelValueRadioSelectedCollection.value === '';

  for (const col of collectionMatches.value) {
    col.checked = col.id === modelValueRadioSelectedCollection.value;
    foundSelectedCollection ||= col.checked;
  }

  for (const col of collectionMatchesSticky.value) {
    col.checked = col.id === modelValueRadioSelectedCollection.value;
    foundSelectedCollection ||= col.checked;
  }

  if (!foundSelectedCollection) {
    // console.log('>> ' + idSubfix + ': Externally changed selected collection to something unknown');
    await setupWithExternalData();
  }
}

watch(modelValueRadioSelectedCollection, correctRadioButtonsChecked);

function onRadioClick(e) {
  // console.log('OnRadioClick');
  if (e.target.value === modelValueRadioSelectedCollection.value) {
    // console.log('  clicked active button');
    emit('update:modelValue', '');
  } else {
    // console.log('  clicked another one');
    emit('update:modelValue', e.target.value);
  }

  correctRadioButtonsChecked();
}

async function fetchMatchedCollections() {
  // console.log(`Need to fetch data for ${collectionSearchString.value}`);
  try {
    const { data } = await useFetch<any>(
      `${getApiRoot()}/collections?size=${numberOfCollectionsToShow}&interval=30&include_zero_volume=true&search=${
        collectionSearchString.value
      }`
    );
    const collections = data.value.items;

    const newMatches = [];
    let needToMakeSticky =
      modelValueRadioSelectedCollection.value !== '' &&
      (collectionMatchesSticky.value.length === 0 ||
        collectionMatchesSticky.value[0].id !== modelValueRadioSelectedCollection.value);
    let needToMakeUnsticky =
      modelValueRadioSelectedCollection.value === '' ||
      (collectionMatchesSticky.value.length > 0 && !collectionMatchesSticky.value[0].checked);

    for (const collection of collections) {
      let checked = false;
      if (collection.id === modelValueRadioSelectedCollection.value) {
        needToMakeSticky = false;
        checked = true;
      }

      if (collectionMatchesSticky.value.length !== 0 && collectionMatchesSticky.value[0].id === collection.id) {
        // console.log('Need to make it un-sticky: ' + item.name);
        needToMakeUnsticky = true;
        checked = collectionMatchesSticky.value[0].checked;
      }

      newMatches.push({
        id: collection.id,
        name: collection.name,
        thumbnail_uri: collection.thumbnail_uri,
        checked,
      });
    }

    if (needToMakeSticky) {
      // console.log('Need to make it sticky!');
      if (collectionMatchesSticky.value.length !== 0) {
        collectionMatchesSticky.value[0].checked = false;
        collectionMatchesSticky.value = [];
      }
      for (const col of collectionMatches.value) {
        if (col.id === modelValueRadioSelectedCollection.value) {
          // console.log('Found entry to make sticky. Name: ' + col.name + ' Checked: ' + col.checked);
          collectionMatchesSticky.value.push(col);
          collectionMatchesSticky.value[0].checked = true;
        }
      }
    } else if (needToMakeUnsticky) {
      collectionMatchesSticky.value = [];
    }

    collectionMatches.value = newMatches;
    correctRadioButtonsChecked();
  } catch (e) {
    console.log('Error while fetching data', e);
  }
}

watchDebounced(collectionSearchString, fetchMatchedCollections, { debounce: 200 });

await setupWithExternalData();
</script>

<template>
  <div class="block w-full flex-col space-y-3">
    <!-- {{ idSubfix }} -->
    <input
      v-model="collectionSearchString"
      class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-black focus:ring-black dark:border-neutral-600 dark:bg-neutral-800 dark:text-white sm:text-sm"
      :placeholder="$t('activity.filter')"
      type="text"
    />
    <div v-for="col in collectionMatchesSticky" :key="`${col.id}_sticky`" class="flex items-center px-0.5">
      <label
        :for="col.id + idSubfix"
        class="flex flex-1 items-center gap-1 text-sm text-neutral-600 dark:text-neutral-300"
      >
        <img
          v-if="col.thumbnail_uri"
          :alt="`${col.name} image`"
          :src="col.thumbnail_uri"
          loading="lazy"
          class="aspect-1 h-5 w-5 rounded-md object-cover"
        />
        {{ col.name }}
      </label>
      <input
        :id="col.id + idSubfix"
        type="radio"
        :name="'radioGroup_CollectionSelect' + idSubfix"
        :value="col.id"
        :checked="col.checked"
        class="h-4 w-4 rounded border border-neutral-300 text-black focus:ring-black dark:border-neutral-600 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
        @click="onRadioClick"
      />
    </div>
    <div v-for="col in collectionMatches" :key="col.id" class="flex items-center px-0.5">
      <label
        :for="col.id + idSubfix"
        class="flex flex-1 items-center gap-1 text-sm text-neutral-600 dark:text-neutral-300"
      >
        <img
          v-if="col.thumbnail_uri"
          :alt="`${col.name} image`"
          :src="col.thumbnail_uri"
          loading="lazy"
          class="aspect-1 h-5 w-5 rounded-md object-cover"
        />
        {{ col.name }}
      </label>
      <input
        :id="col.id + idSubfix"
        type="radio"
        :name="'radioGroup_CollectionSelect' + idSubfix"
        :value="col.id"
        :checked="col.checked"
        class="h-4 w-4 rounded border border-neutral-300 text-black focus:ring-black dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
        @click="onRadioClick"
      />
    </div>
  </div>
</template>
